//src/utils/analytics.js
// Comprehensive Analytics & Monitoring with Firebase Analytics integration

import { getAnalytics, logEvent, setUserId, setUserProperties } from 'firebase/analytics';
import posthog from 'posthog-js';
import logger from '../core/logger';


class AnalyticsManager {
  constructor() {
    this.isEnabled = true; // Always enabled for better monitoring
    this.sessionId = this.generateSessionId();
    this.events = [];
    this.maxEvents = 100; // Keep only recent events in memory
    this.firebaseAnalytics = null;

    // Initialize Firebase Analytics
    this.initializeFirebaseAnalytics();

    // Initialize tracking
    this.initializeTracking();
  }

  initializeFirebaseAnalytics() {
    try {
      if (typeof window !== 'undefined') {
        this.firebaseAnalytics = getAnalytics();
        if (import.meta.env.DEV) {
          logger.debug('📊 Firebase Analytics initialized');
        }
      }
    } catch (error) {
      logger.warn('Firebase Analytics initialization failed:', error);
    }
  }

  generateSessionId() {
    // Generate a random session ID (not tied to user)
    return 'sess_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
  }

  initializeTracking() {
    // Track page visibility changes for session analytics
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.trackEvent('session_pause');
      } else {
        this.trackEvent('session_resume');
      }
    });

    // Track app errors (privacy-safe)
    window.addEventListener('error', (event) => {
      this.trackEvent('error_shown', {
        error_type: 'javascript_error',
        file: event.filename ? this.sanitizeFilename(event.filename) : 'unknown',
        line: event.lineno || 0
      });
    });

    window.addEventListener('unhandledrejection', (event) => {
      this.trackEvent('error_shown', {
        error_type: 'unhandled_rejection',
        message: this.sanitizeErrorMessage(event.reason?.message)
      });
    });

    // Track app lifecycle
    this.trackEvent('app_start', {
      timestamp: new Date().toISOString(),
      user_agent: navigator.userAgent.split(' ')[0], // Just the browser
      viewport: `${window.innerWidth}x${window.innerHeight}`
    });
  }

  sanitizeFilename(filename) {
    // Remove personal paths, keep only relative path
    return filename.replace(/.*\/src\//, 'src/').replace(/.*\/public\//, 'public/');
  }

  sanitizeErrorMessage(message) {
    if (!message) return 'unknown';
    // Remove potentially sensitive information
    return message.substring(0, 100).replace(/\b\d{10,}\b/g, '[number]');
  }

  trackEvent(eventName, properties = {}) {
    const event = {
      event: eventName,
      session_id: this.sessionId,
      timestamp: new Date().toISOString(),
      platform: this.getPlatform(),
      ...properties
    };

    // Log to Firebase Analytics
    if (this.firebaseAnalytics) {
      try {
        logEvent(this.firebaseAnalytics, eventName, properties);
      } catch (error) {
        logger.error('Firebase Analytics error:', error);
      }
    }

    // Log to PostHog
    if (posthog.__loaded) {
      try {
        posthog.capture(eventName, properties);
      } catch (error) {
        logger.error('PostHog Analytics error:', error);
      }
    }

    // Add to in-memory queue
    this.events.push(event);

    // Keep only recent events
    if (this.events.length > this.maxEvents) {
      this.events = this.events.slice(-this.maxEvents);
    }

    // Store locally for debugging
    this.storeEventLocally(event);

    // Log in development
    if (import.meta.env.DEV) {
      logger.debug(`📊 [Analytics] ${eventName}`, properties);
    }
  }

  getPlatform() {
    if (typeof window === 'undefined') return 'server';

    const ua = window.navigator.userAgent.toLowerCase();

    if (/iphone|ipad|ipod/.test(ua)) return 'ios';
    if (/android/.test(ua)) return 'android';
    if (/windows/.test(ua)) return 'windows';
    if (/mac/.test(ua)) return 'macos';
    if (/linux/.test(ua)) return 'linux';

    return 'unknown';
  }

  storeEventLocally(event) {
    try {
      // Store in localStorage for debugging (limit storage)
      const stored = JSON.parse(localStorage.getItem('clarity_analytics') || '[]');
      stored.push(event);
      
      // Keep only last 50 events in storage
      const limited = stored.slice(-50);
      localStorage.setItem('clarity_analytics', JSON.stringify(limited));
    } catch (error) {
      logger.warn('[Analytics] Failed to store event locally:', error);
    }
  }

  // ═══════════════════════════════════════════════════════════════
  // JOURNAL ENTRY TRACKING
  // ═══════════════════════════════════════════════════════════════

  trackEntryCreationStarted(mode = 'general', entryType = 'text') {
    this.trackEvent('entry_creation_started', { mode, entry_type: entryType });
  }

  trackEntrySaved(options = {}) {
    this.trackEvent('entry_saved', {
      mode: options.mode || 'text',
      word_count: options.wordCount || 0,
      has_mood: options.hasMood || false,
      has_weather: options.hasWeather || false,
      has_image: options.hasImage || false,
      content_length: options.contentLength || 0
    });
  }

  trackEntrySavedAsDraft(wordCount = 0) {
    this.trackEvent('entry_saved_as_draft', { word_count: wordCount });
  }

  trackEntryDiscarded(wordCount = 0, timeSpent = 0) {
    this.trackEvent('entry_discard_confirmed', {
      word_count: wordCount,
      time_spent_seconds: Math.round(timeSpent / 1000)
    });
  }

  trackMoodSelected(mood, source = 'manual') {
    this.trackEvent('entry_mood_selected', { mood, source });
  }

  trackWritingStreakAchieved(streakDays, previousBest = 0) {
    this.trackEvent('writing_streak_achieved', {
      streak_days: streakDays,
      previous_best: previousBest
    });
  }

  // Legacy methods for backwards compatibility
  trackJournalEntry() {
    this.trackEntrySaved({ mode: 'text' });
  }

  trackPhotoJournalEntry() {
    this.trackEntrySaved({ mode: 'photo', hasImage: true });
  }

  // ═══════════════════════════════════════════════════════════════
  // AI FEATURES TRACKING
  // ═══════════════════════════════════════════════════════════════

  trackAIAnalysisRequested(wordCount = 0) {
    this.trackEvent('ai_analysis_requested', { word_count: wordCount });
  }

  trackAIAnalysisCompleted(options = {}) {
    this.trackEvent('ai_analysis_completed', {
      processing_time_ms: options.processingTime || 0,
      topics_count: options.topicsCount || 0,
      analysis_length: options.analysisLength || 0
    });
  }

  trackAIAnalysisFailed(errorMessage = '') {
    this.trackEvent('ai_analysis_failed', {
      error_message: this.sanitizeErrorMessage(errorMessage)
    });
  }

  trackAIPromptsGenerated(promptCount = 0) {
    this.trackEvent('ai_prompts_generated', { prompt_count: promptCount });
  }

  trackAIPromptApplied(promptIndex = 0) {
    this.trackEvent('ai_prompt_applied', { prompt_index: promptIndex });
  }

  // Legacy method
  trackAIFeedbackRequest(success = true) {
    if (success) {
      this.trackAIAnalysisCompleted({});
    } else {
      this.trackAIAnalysisFailed('Unknown error');
    }
  }

  // ═══════════════════════════════════════════════════════════════
  // OCR / HANDWRITING TRACKING
  // ═══════════════════════════════════════════════════════════════

  trackOCRUploadStarted(uploadMethod = 'gallery') {
    this.trackEvent('ocr_upload_started', { upload_method: uploadMethod });
  }

  trackOCRFileSelected(options = {}) {
    this.trackEvent('ocr_file_selected', {
      file_size: options.fileSize || 0,
      resolution: options.resolution || 'unknown'
    });
  }

  trackOCRProcessingStarted() {
    this.trackEvent('ocr_processing_started');
  }

  trackOCRProcessingCompleted(options = {}) {
    this.trackEvent('ocr_processing_completed', {
      confidence: options.confidence || 0,
      text_length: options.textLength || 0,
      processing_time_ms: options.processingTime || 0
    });
  }

  trackOCRProcessingFailed(errorMessage = '') {
    this.trackEvent('ocr_processing_failed', {
      error_message: this.sanitizeErrorMessage(errorMessage)
    });
  }

  trackOCRTextSaved(options = {}) {
    this.trackEvent('ocr_text_saved', {
      final_text_length: options.textLength || 0,
      page_count: options.pageCount || 1,
      processing_time_ms: options.processingTime || 0
    });
  }

  trackOCRCancelled(step = 'unknown') {
    this.trackEvent('ocr_cancelled', { step });
  }

  // Legacy method
  trackOCRUsed(success = true, processingTime = null) {
    if (success) {
      this.trackOCRProcessingCompleted({ processingTime });
    } else {
      this.trackOCRProcessingFailed('Unknown error');
    }
  }

  // ═══════════════════════════════════════════════════════════════
  // VOICE RECORDING TRACKING
  // ═══════════════════════════════════════════════════════════════

  trackVoiceRecordingStarted() {
    this.trackEvent('voice_recording_started');
  }

  trackVoiceRecordingStopped(options = {}) {
    this.trackEvent('voice_recording_stopped', {
      duration_seconds: options.duration || 0,
      text_length: options.textLength || 0,
      success: options.success !== false
    });
  }

  trackVoiceRecordingError(errorCode = 'unknown') {
    this.trackEvent('voice_recording_error', { error_code: errorCode });
  }

  // ═══════════════════════════════════════════════════════════════
  // GOAL TRACKING
  // ═══════════════════════════════════════════════════════════════

  trackGoalCreated(options = {}) {
    this.trackEvent('goal_created', {
      goal_type: options.goalType || 'habit',
      tracking_source: options.trackingSource || 'manual',
      category: options.category || 'other',
      target_value: options.targetValue || 0,
      timeframe: options.timeframe || 'daily',
      has_description: options.hasDescription || false,
      has_milestones: options.hasMilestones || false,
    });
  }

  trackGoalCompleted(goalId, timeToCompletionDays = 0) {
    this.trackEvent('goal_completed', {
      goal_id: goalId,
      time_to_completion_days: timeToCompletionDays
    });
  }

  trackGoalProgressUpdated(completionPercentage = 0) {
    this.trackEvent('goal_progress_updated', {
      completion_percentage: completionPercentage
    });
  }

  trackGoalDeleted(goalId, completionPercentage = 0) {
    this.trackEvent('goal_deleted', {
      goal_id: goalId,
      completion_percentage: completionPercentage
    });
  }

  // ═══════════════════════════════════════════════════════════════
  // INSIGHTS PAGE TRACKING
  // ═══════════════════════════════════════════════════════════════

  trackInsightsPageViewed() {
    this.trackEvent('insights_page_viewed');
  }

  trackInsightsTabSwitched(fromTab, toTab) {
    this.trackEvent('insights_tab_switched', {
      from_tab: fromTab,
      to_tab: toTab
    });
  }

  trackDateRangeSelected(dateRange, entriesInRange = 0) {
    this.trackEvent('date_range_selected', {
      date_range: dateRange,
      entries_in_range: entriesInRange
    });
  }

  trackInsightsChartInteraction(chartType, interactionType = 'view') {
    this.trackEvent('insights_chart_interaction', {
      chart_type: chartType,
      interaction_type: interactionType
    });
  }

  // ═══════════════════════════════════════════════════════════════
  // PWA TRACKING (Enhanced)
  // ═══════════════════════════════════════════════════════════════

  // Get browser info for analytics
  getBrowserInfo() {
    const ua = navigator.userAgent;
    if (/Safari/.test(ua) && !/Chrome/.test(ua)) return 'safari';
    if (/Chrome/.test(ua) && !/Chromium/.test(ua) && !/Edge/.test(ua)) return 'chrome';
    if (/Firefox/.test(ua)) return 'firefox';
    if (/Edge|Edg/.test(ua)) return 'edge';
    if (/SamsungBrowser/.test(ua)) return 'samsung';
    return 'other';
  }

  // Get common PWA event properties
  getPWAEventProperties(additionalProps = {}) {
    return {
      platform: this.getPlatform(),
      browser: this.getBrowserInfo(),
      is_standalone: window.matchMedia ? window.matchMedia('(display-mode: standalone)').matches : false,
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
      ...additionalProps
    };
  }

  // ─────────────────────────────────────────────────────────────────
  // Banner Display Events
  // ─────────────────────────────────────────────────────────────────

  trackPWAInstallBannerShown(options = {}) {
    this.trackEvent('pwa_install_banner_shown', this.getPWAEventProperties({
      page_location: options.pageLocation || 'unknown',
      banner_position: options.bannerPosition || 'bottom',
      time_to_show_ms: options.timeToShow || 0,
      install_method: options.installMethod || 'unknown', // 'ios-safari' | 'android' | 'chromium'
      is_returning_user: options.isReturningUser || false
    }));
  }

  trackPWABannerDismissed(options = {}) {
    this.trackEvent('pwa_banner_dismissed', this.getPWAEventProperties({
      page_location: options.pageLocation || 'unknown',
      banner_position: options.bannerPosition || 'bottom',
      time_visible_ms: options.timeVisible || 0,
      install_method: options.installMethod || 'unknown'
    }));
  }

  // ─────────────────────────────────────────────────────────────────
  // iOS Safari Instruction Events
  // ─────────────────────────────────────────────────────────────────

  trackIOSInstructionsShown(options = {}) {
    this.trackEvent('pwa_ios_instructions_shown', this.getPWAEventProperties({
      page_location: options.pageLocation || 'unknown',
      is_login_page: options.isLoginPage || false
    }));
  }

  trackIOSStepViewed(step, options = {}) {
    this.trackEvent('pwa_ios_step_viewed', this.getPWAEventProperties({
      step_number: step,
      step_name: step === 1 ? 'tap_share' : step === 2 ? 'add_to_home' : 'tap_add',
      page_location: options.pageLocation || 'unknown'
    }));
  }

  trackIOSInstructionsDismissed(options = {}) {
    this.trackEvent('pwa_ios_instructions_dismissed', this.getPWAEventProperties({
      page_location: options.pageLocation || 'unknown',
      steps_viewed: options.stepsViewed || 0,
      time_visible_ms: options.timeVisible || 0
    }));
  }

  // ─────────────────────────────────────────────────────────────────
  // Android / Chromium Install Events
  // ─────────────────────────────────────────────────────────────────

  trackAndroidInstallPrompted(options = {}) {
    this.trackEvent('pwa_android_install_prompted', this.getPWAEventProperties({
      page_location: options.pageLocation || 'unknown',
      is_login_page: options.isLoginPage || false
    }));
  }

  trackAndroidInstallClicked(options = {}) {
    this.trackEvent('pwa_android_install_clicked', this.getPWAEventProperties({
      page_location: options.pageLocation || 'unknown',
      time_to_click_ms: options.timeToClick || 0
    }));
  }

  trackPWAInstallAccepted(platform = 'unknown', options = {}) {
    this.trackEvent('pwa_install_accepted', this.getPWAEventProperties({
      install_platform: platform,
      page_location: options.pageLocation || 'unknown',
      is_login_page: options.isLoginPage || false
    }));
  }

  trackPWAInstallRejected(options = {}) {
    this.trackEvent('pwa_install_rejected', this.getPWAEventProperties({
      page_location: options.pageLocation || 'unknown',
      time_to_reject_ms: options.timeToReject || 0
    }));
  }

  trackPWAInstallCompleted(platform = 'unknown', options = {}) {
    this.trackEvent('pwa_install_completed', this.getPWAEventProperties({
      install_platform: platform,
      page_location: options.pageLocation || 'unknown',
      time_to_install_ms: options.timeToInstall || 0
    }));
  }

  trackPWAInstallFailed(options = {}) {
    this.trackEvent('pwa_install_failed', this.getPWAEventProperties({
      error_message: options.errorMessage || 'unknown',
      page_location: options.pageLocation || 'unknown'
    }));
  }

  // ─────────────────────────────────────────────────────────────────
  // Login Page Specific Events
  // ─────────────────────────────────────────────────────────────────

  trackPWABannerShownLoginPage(options = {}) {
    this.trackEvent('pwa_banner_shown_login_page', this.getPWAEventProperties({
      banner_position: options.bannerPosition || 'top',
      install_method: options.installMethod || 'unknown',
      time_to_show_ms: options.timeToShow || 0
    }));
  }

  trackPWALoginInstallAttempt(options = {}) {
    this.trackEvent('pwa_login_install_attempt', this.getPWAEventProperties({
      install_method: options.installMethod || 'unknown'
    }));
  }

  // ─────────────────────────────────────────────────────────────────
  // Funnel Tracking
  // ─────────────────────────────────────────────────────────────────

  trackPWAFunnelStep(step, options = {}) {
    // Steps: 1=banner_shown, 2=install_clicked, 3=prompt_shown, 4=install_confirmed, 5=app_opened_standalone
    const stepNames = {
      1: 'banner_shown',
      2: 'install_clicked',
      3: 'prompt_shown',
      4: 'install_confirmed',
      5: 'app_opened_standalone'
    };

    this.trackEvent('pwa_install_funnel', this.getPWAEventProperties({
      funnel_step: step,
      funnel_step_name: stepNames[step] || 'unknown',
      install_method: options.installMethod || 'unknown',
      page_location: options.pageLocation || 'unknown',
      is_login_page: options.isLoginPage || false,
      time_in_funnel_ms: options.timeInFunnel || 0
    }));
  }

  // ─────────────────────────────────────────────────────────────────
  // Standalone Mode Detection
  // ─────────────────────────────────────────────────────────────────

  trackPWAStandaloneLaunch() {
    const isStandalone = (window.matchMedia ? window.matchMedia('(display-mode: standalone)').matches : false) ||
                         window.navigator.standalone === true;

    if (isStandalone) {
      this.trackEvent('pwa_standalone_launch', this.getPWAEventProperties({
        launch_source: document.referrer ? 'link' : 'home_screen'
      }));
    }
  }

  // ─────────────────────────────────────────────────────────────────
  // Offline Events (unchanged)
  // ─────────────────────────────────────────────────────────────────

  trackOfflineEntrySynced(timeSinceCreationSeconds = 0) {
    this.trackEvent('offline_entry_synced', {
      time_since_creation_seconds: timeSinceCreationSeconds
    });
  }

  trackOfflineModeEnabled() {
    this.trackEvent('offline_mode_enabled');
  }

  trackOfflineModeDisabled(offlineDurationSeconds = 0) {
    this.trackEvent('offline_mode_disabled', {
      offline_duration_seconds: offlineDurationSeconds
    });
  }

  // ═══════════════════════════════════════════════════════════════
  // ONBOARDING TRACKING
  // ═══════════════════════════════════════════════════════════════

  /**
   * Track when onboarding flow starts (user sees first screen)
   */
  trackOnboardingStarted(options = {}) {
    this.trackEvent('onboarding_started', {
      auth_method: options.authMethod || 'unknown', // 'google' | 'email'
      platform: this.getPlatform(),
      is_new_user: options.isNewUser !== false
    });
  }

  /**
   * Track each step view in the onboarding flow
   */
  trackOnboardingStepViewed(step, options = {}) {
    const stepNames = {
      'welcome': 'Welcome',
      'birthDate': 'Birth Date',
      'birthTime': 'Birth Time',
      'birthPlace': 'Birth Place',
      'calculating': 'Calculating',
      'reveal': 'Reveal'
    };

    this.trackEvent('onboarding_step_viewed', {
      step_id: step,
      step_name: stepNames[step] || step,
      step_number: options.stepNumber || 0,
      time_on_previous_step_ms: options.timeOnPreviousStep || 0
    });
  }

  /**
   * Track when user completes onboarding successfully
   */
  trackOnboardingCompleted(options = {}) {
    this.trackEvent('onboarding_completed', {
      auth_method: options.authMethod || 'unknown',
      total_time_ms: options.totalTime || 0,
      steps_completed: options.stepsCompleted || 0,
      has_birth_date: options.hasBirthDate || false,
      has_birth_time: options.hasBirthTime || false,
      has_birth_place: options.hasBirthPlace || false,
      platform: this.getPlatform()
    });
  }

  /**
   * Track when user skips onboarding
   */
  trackOnboardingSkipped(options = {}) {
    this.trackEvent('onboarding_skipped', {
      skipped_at_step: options.skippedAtStep || 'welcome',
      time_before_skip_ms: options.timeBeforeSkip || 0,
      auth_method: options.authMethod || 'unknown',
      platform: this.getPlatform()
    });
  }

  /**
   * Track onboarding errors
   */
  trackOnboardingError(options = {}) {
    this.trackEvent('onboarding_error', {
      error_type: options.errorType || 'unknown',
      error_message: this.sanitizeErrorMessage(options.errorMessage),
      step: options.step || 'unknown',
      auth_method: options.authMethod || 'unknown'
    });
  }

  /**
   * Track onboarding status check result
   */
  trackOnboardingStatusChecked(options = {}) {
    this.trackEvent('onboarding_status_checked', {
      needs_onboarding: options.needsOnboarding || false,
      profile_exists: options.profileExists || false,
      has_completed: options.hasCompleted || false,
      has_skipped: options.hasSkipped || false,
      auth_method: options.authMethod || 'unknown'
    });
  }

  // ═══════════════════════════════════════════════════════════════
  // ENGAGEMENT TRACKING
  // ═══════════════════════════════════════════════════════════════

  trackSessionStarted() {
    this.sessionStartTime = Date.now();
    this.trackEvent('session_started', { platform: this.getPlatform() });
  }

  trackFeatureFirstUsed(featureName) {
    try {
      const usedFeatures = JSON.parse(localStorage.getItem('clarity_used_features') || '[]');
      if (!usedFeatures.includes(featureName)) {
        usedFeatures.push(featureName);
        localStorage.setItem('clarity_used_features', JSON.stringify(usedFeatures));
        this.trackEvent('feature_first_used', { feature_name: featureName });
      }
    } catch {
      // localStorage unavailable (WKWebView ITP) — still fire the analytics event
      this.trackEvent('feature_first_used', { feature_name: featureName });
    }
  }

  trackPageNavigation(fromPage, toPage) {
    this.trackEvent('page_navigation', { from_page: fromPage, to_page: toPage });
  }

  trackSearchExecuted(resultsCount = 0, hasFilters = false) {
    this.trackEvent('search_executed', {
      results_count: resultsCount,
      has_filters: hasFilters
    });
  }

  // ═══════════════════════════════════════════════════════════════
  // ERROR TRACKING
  // ═══════════════════════════════════════════════════════════════

  trackError(errorType, context = {}) {
    this.trackEvent('error_shown', {
      error_type: errorType,
      context: this.sanitizeContext(context)
    });
  }

  trackNetworkError(endpoint, errorCode = 'unknown') {
    this.trackEvent('network_error_occurred', {
      endpoint: this.sanitizeEndpoint(endpoint),
      error_code: errorCode
    });
  }

  sanitizeEndpoint(endpoint) {
    // Remove query params and sensitive path segments
    if (!endpoint) return 'unknown';
    return endpoint.split('?')[0].replace(/\/[a-zA-Z0-9]{20,}/g, '/[id]');
  }

  sanitizeContext(context) {
    // Remove sensitive data from context
    const sanitized = {};
    Object.keys(context).forEach(key => {
      const value = context[key];
      if (typeof value === 'string' && value.length > 200) {
        sanitized[key] = value.substring(0, 200) + '...';
      } else if (typeof value === 'object') {
        sanitized[key] = '[object]';
      } else {
        sanitized[key] = value;
      }
    });
    return sanitized;
  }

  // ═══════════════════════════════════════════════════════════════
  // AUTHENTICATION TRACKING
  // ═══════════════════════════════════════════════════════════════

  // Set user ID for analytics
  setUser(userId) {
    if (this.firebaseAnalytics && userId) {
      try {
        setUserId(this.firebaseAnalytics, userId);
      } catch (error) {
        logger.error('Failed to set user ID:', error);
      }
    }
  }

  // Set user properties
  setUserProperties(properties) {
    if (this.firebaseAnalytics) {
      try {
        setUserProperties(this.firebaseAnalytics, properties);
      } catch (error) {
        logger.error('Failed to set user properties:', error);
      }
    }
  }

  // Track sign-up attempts and outcomes
  trackSignupAttempt(method) {
    this.trackEvent('signup_attempt', { method });
  }

  trackSignupSuccess(method, userId) {
    this.trackEvent('signup_success', { method });
    this.setUser(userId);
  }

  trackSignupFailure(method, errorCode, errorMessage) {
    this.trackEvent('signup_failure', {
      method,
      error_code: errorCode,
      error_message: errorMessage
    });
  }

  // Track login attempts and outcomes
  trackLoginAttempt(method, rememberMe = false) {
    this.trackEvent('login_attempt', { method, remember_me: rememberMe });
  }

  trackLoginSuccess(method, userId, rememberMe = false) {
    this.trackEvent('login_success', { method, remember_me: rememberMe });
    this.setUser(userId);
  }

  trackLoginFailure(method, errorCode, errorMessage) {
    this.trackEvent('login_failure', {
      method,
      error_code: errorCode,
      error_message: errorMessage
    });
  }

  // Track biometric auth
  trackBiometricRegistration(success, errorMessage = null) {
    this.trackEvent('biometric_registration', {
      success,
      error_message: errorMessage
    });
  }

  trackBiometricLogin(success, errorMessage = null) {
    this.trackEvent('biometric_login', {
      success,
      error_message: errorMessage
    });
  }

  // Track password reset
  trackPasswordResetRequest(success, errorMessage = null) {
    this.trackEvent('password_reset_request', {
      success,
      error_message: errorMessage
    });
  }

  // Track logout
  trackLogout(userId) {
    this.trackEvent('logout', { user_id: userId });
  }

  // Track session events
  startSession() {
    this.sessionStartTime = Date.now();
    this.trackEvent('session_start');
  }

  endSession() {
    if (this.sessionStartTime) {
      const duration = Math.round((Date.now() - this.sessionStartTime) / 1000);
      this.trackEvent('session_end', { duration_seconds: duration });
      this.sessionStartTime = null;
    }
  }

  // Track user preferences
  trackUserPreferences(preferences) {
    this.setUserProperties({
      remember_me_enabled: preferences.rememberMe || false,
      biometric_enabled: preferences.biometricEnabled || false,
      theme: preferences.theme || 'system'
    });
  }

  // ═══════════════════════════════════════════════════════════════
  // PERFORMANCE TRACKING
  // ═══════════════════════════════════════════════════════════════

  trackPerformance(metricName, durationMs, context = {}) {
    this.trackEvent('performance_metric', {
      metric: metricName,
      duration_ms: durationMs,
      ...context
    });
  }

  async measureAuthPerformance(operationName, operation) {
    const startTime = Date.now();

    try {
      const result = await operation();
      const duration = Date.now() - startTime;

      this.trackPerformance(`auth_${operationName}`, duration, { success: true });

      return result;
    } catch (error) {
      const duration = Date.now() - startTime;

      this.trackPerformance(`auth_${operationName}`, duration, {
        success: false,
        error: error.code || error.message
      });

      throw error;
    }
  }

  // Get analytics summary for debugging
  getAnalyticsSummary() {
    try {
      const stored = JSON.parse(localStorage.getItem('clarity_analytics') || '[]');
      const eventCounts = {};

      stored.forEach(event => {
        eventCounts[event.event] = (eventCounts[event.event] || 0) + 1;
      });

      return {
        total_events: stored.length,
        session_id: this.sessionId,
        event_counts: eventCounts,
        last_events: stored.slice(-10).map(e => ({
          event: e.event,
          timestamp: e.timestamp
        }))
      };
    } catch {
      return { error: 'Failed to retrieve analytics data' };
    }
  }

  // Clear analytics data (for privacy)
  clearAnalytics() {
    this.events = [];
    try { localStorage.removeItem('clarity_analytics'); } catch { /* WKWebView ITP guard */ }
    logger.debug('[Analytics] Data cleared');
  }
}

// Create singleton instance
const analytics = new AnalyticsManager();

// Export convenience methods - Legacy
export const trackJournalEntry = () => analytics.trackJournalEntry();
export const trackPhotoJournalEntry = () => analytics.trackPhotoJournalEntry();
export const trackAIFeedbackRequest = (success) => analytics.trackAIFeedbackRequest(success);
export const trackOCRUsed = (success, processingTime) => analytics.trackOCRUsed(success, processingTime);
export const trackError = (errorType, context) => analytics.trackError(errorType, context);
export const getAnalyticsSummary = () => analytics.getAnalyticsSummary();
export const clearAnalytics = () => analytics.clearAnalytics();

// Journal Entry Events
export const trackEntryCreationStarted = (mode, entryType) => analytics.trackEntryCreationStarted(mode, entryType);
export const trackEntrySaved = (options) => analytics.trackEntrySaved(options);
export const trackEntrySavedAsDraft = (wordCount) => analytics.trackEntrySavedAsDraft(wordCount);
export const trackEntryDiscarded = (wordCount, timeSpent) => analytics.trackEntryDiscarded(wordCount, timeSpent);
export const trackMoodSelected = (mood, source) => analytics.trackMoodSelected(mood, source);
export const trackWritingStreakAchieved = (streakDays, previousBest) => analytics.trackWritingStreakAchieved(streakDays, previousBest);

// AI Feature Events
export const trackAIAnalysisRequested = (wordCount) => analytics.trackAIAnalysisRequested(wordCount);
export const trackAIAnalysisCompleted = (options) => analytics.trackAIAnalysisCompleted(options);
export const trackAIAnalysisFailed = (errorMessage) => analytics.trackAIAnalysisFailed(errorMessage);
export const trackAIPromptsGenerated = (promptCount) => analytics.trackAIPromptsGenerated(promptCount);
export const trackAIPromptApplied = (promptIndex) => analytics.trackAIPromptApplied(promptIndex);

// OCR Events
export const trackOCRUploadStarted = (uploadMethod) => analytics.trackOCRUploadStarted(uploadMethod);
export const trackOCRFileSelected = (options) => analytics.trackOCRFileSelected(options);
export const trackOCRProcessingStarted = () => analytics.trackOCRProcessingStarted();
export const trackOCRProcessingCompleted = (options) => analytics.trackOCRProcessingCompleted(options);
export const trackOCRProcessingFailed = (errorMessage) => analytics.trackOCRProcessingFailed(errorMessage);
export const trackOCRTextSaved = (options) => analytics.trackOCRTextSaved(options);
export const trackOCRCancelled = (step) => analytics.trackOCRCancelled(step);

// Voice Recording Events
export const trackVoiceRecordingStarted = () => analytics.trackVoiceRecordingStarted();
export const trackVoiceRecordingStopped = (options) => analytics.trackVoiceRecordingStopped(options);
export const trackVoiceRecordingError = (errorCode) => analytics.trackVoiceRecordingError(errorCode);

// Goal Events
export const trackGoalCreated = (options) => analytics.trackGoalCreated(options);
export const trackGoalCompleted = (goalId, timeToCompletionDays) => analytics.trackGoalCompleted(goalId, timeToCompletionDays);
export const trackGoalProgressUpdated = (completionPercentage) => analytics.trackGoalProgressUpdated(completionPercentage);
export const trackGoalDeleted = (goalId, completionPercentage) => analytics.trackGoalDeleted(goalId, completionPercentage);

// Insights Page Events
export const trackInsightsPageViewed = () => analytics.trackInsightsPageViewed();
export const trackInsightsTabSwitched = (fromTab, toTab) => analytics.trackInsightsTabSwitched(fromTab, toTab);
export const trackDateRangeSelected = (dateRange, entriesInRange) => analytics.trackDateRangeSelected(dateRange, entriesInRange);
export const trackInsightsChartInteraction = (chartType, interactionType) => analytics.trackInsightsChartInteraction(chartType, interactionType);

// PWA Events (Enhanced)
export const trackPWAInstallBannerShown = (options) => analytics.trackPWAInstallBannerShown(options);
export const trackPWABannerDismissed = (options) => analytics.trackPWABannerDismissed(options);
export const trackPWAInstallAccepted = (platform, options) => analytics.trackPWAInstallAccepted(platform, options);
export const trackPWAInstallRejected = (options) => analytics.trackPWAInstallRejected(options);
export const trackPWAInstallCompleted = (platform, options) => analytics.trackPWAInstallCompleted(platform, options);
export const trackPWAInstallFailed = (options) => analytics.trackPWAInstallFailed(options);

// PWA iOS Events
export const trackIOSInstructionsShown = (options) => analytics.trackIOSInstructionsShown(options);
export const trackIOSStepViewed = (step, options) => analytics.trackIOSStepViewed(step, options);
export const trackIOSInstructionsDismissed = (options) => analytics.trackIOSInstructionsDismissed(options);

// PWA Android Events
export const trackAndroidInstallPrompted = (options) => analytics.trackAndroidInstallPrompted(options);
export const trackAndroidInstallClicked = (options) => analytics.trackAndroidInstallClicked(options);

// PWA Login Page Events
export const trackPWABannerShownLoginPage = (options) => analytics.trackPWABannerShownLoginPage(options);
export const trackPWALoginInstallAttempt = (options) => analytics.trackPWALoginInstallAttempt(options);

// PWA Funnel & Standalone
export const trackPWAFunnelStep = (step, options) => analytics.trackPWAFunnelStep(step, options);
export const trackPWAStandaloneLaunch = () => analytics.trackPWAStandaloneLaunch();

// PWA Offline Events
export const trackOfflineEntrySynced = (timeSinceCreationSeconds) => analytics.trackOfflineEntrySynced(timeSinceCreationSeconds);
export const trackOfflineModeEnabled = () => analytics.trackOfflineModeEnabled();
export const trackOfflineModeDisabled = (offlineDurationSeconds) => analytics.trackOfflineModeDisabled(offlineDurationSeconds);

// Onboarding Events
export const trackOnboardingStarted = (options) => analytics.trackOnboardingStarted(options);
export const trackOnboardingStepViewed = (step, options) => analytics.trackOnboardingStepViewed(step, options);
export const trackOnboardingCompleted = (options) => analytics.trackOnboardingCompleted(options);
export const trackOnboardingSkipped = (options) => analytics.trackOnboardingSkipped(options);
export const trackOnboardingError = (options) => analytics.trackOnboardingError(options);
export const trackOnboardingStatusChecked = (options) => analytics.trackOnboardingStatusChecked(options);

// Engagement Events
export const trackSessionStarted = () => analytics.trackSessionStarted();
export const trackFeatureFirstUsed = (featureName) => analytics.trackFeatureFirstUsed(featureName);
export const trackPageNavigation = (fromPage, toPage) => analytics.trackPageNavigation(fromPage, toPage);
export const trackSearchExecuted = (resultsCount, hasFilters) => analytics.trackSearchExecuted(resultsCount, hasFilters);

// Authentication Events (already in class, export convenience)
export const trackLoginAttempt = (method, rememberMe) => analytics.trackLoginAttempt(method, rememberMe);
export const trackLoginSuccess = (method, userId, rememberMe) => analytics.trackLoginSuccess(method, userId, rememberMe);
export const trackLoginFailure = (method, errorCode, errorMessage) => analytics.trackLoginFailure(method, errorCode, errorMessage);
export const trackSignupAttempt = (method) => analytics.trackSignupAttempt(method);
export const trackSignupSuccess = (method, userId) => analytics.trackSignupSuccess(method, userId);
export const trackSignupFailure = (method, errorCode, errorMessage) => analytics.trackSignupFailure(method, errorCode, errorMessage);
export const trackLogout = (userId) => analytics.trackLogout(userId);

export default analytics;